import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createDeveloper, updateDeveloper } from '../api/developersApi'

/**
 * Developer Modal Component
 * Form for creating/editing developers
 */
export function DeveloperModal({ isOpen, onClose, developer }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!developer

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (developer) {
      reset({
        name: developer.name || '',
      })
    } else {
      reset({
        name: '',
      })
    }
  }, [developer, reset])

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      if (isEdit) {
        return updateDeveloper(developer.id, formData)
      } else {
        return createDeveloper(formData)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developers'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit
          ? t('developers.developerUpdated')
          : t('developers.developerCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error.message || t('developers.developerSaveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('developers.editDeveloper') : t('developers.createDeveloper')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Legend fontSize="md" fontWeight="600">
              {t('developers.developerInfo')}
            </Fieldset.Legend>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('developers.name')} *`}
                      placeholder={t('developers.namePlaceholder')}
                      error={errors.name?.message}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              isLoading={saveMutation.isPending}
            >
              {isEdit ? t('common.save') : t('common.create')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}

export default DeveloperModal
